import React, { useContext, useState } from 'react';
import { IconSvg, IconSvgSizes } from '@att-bit/duc.components.icon-svg';
import { Metrics } from '@att-bit/duc.utils.types';
import { AnalyticsEvent } from '@att-bit/ui-frameworks.components.analytics-event';
import { docComplete } from '@att-bit/duc.utils.types';
import { useWindowSize } from '@att-bit/duc.hooks.use-window-size';
import { Modal, ModalSize } from '@att-bit/duc.components.modal';
import { HeadingInterface } from '@att-bit/duc.components.heading';
import { TextInterface } from '@att-bit/duc.components.text';
import { useEventContext } from '@att-bit/duc.utils-react.events-context';
import { useUniqueId } from '@att-bit/duc.utils.custom-useid';
import {
  BackgroundVideo,
  BackgroundVideoInterface,
} from '@att-bit/duc.fragments.background-video';

// Fixing missing file issue1;

export enum ButtonVariant {
  /** Primary Blue */
  BLUE = 'btn-primary',
  /** Primary White */
  WHITE = 'btn-secondary',
  SMALLPRIMARYCTA = 'primary-cta type-sm',
}

export enum ButtonSize {
  LARGE = '',
  SMALL = 'btn-small',
}

export type ButtonProps = {
  /**  children (text/jsx) to be rendered in component  */
  children?: React.ReactNode;
  /** is button disabled or not */
  disabled?: boolean;
  /**  is button full width?  */
  fullWidth?: boolean;
  /**  is button mobile full width?  */
  mobileFullWidth?: boolean;
  /** if button used as link, what is the url */
  href?: string;
  /**  is button used to navigate to another page  */
  isNavigational?: boolean;
  /** metrics reporting object */
  metrics?: Metrics;
  /**  is component on a dark background?  */
  onDark?: boolean;
  /** button size LARGE | SMALL */
  size?: ButtonSize;
  /** show loading spinner */
  spinner?: boolean;
  /** show loading spinner without label */
  spinnerWithoutLabel?: boolean;
  /** aria hidden */
  ariahidden?: string;
  /** svgNodeStr used to display an icon (use only 32px icons) */
  svgNodeStr?: string;
  /** svgUrl used to display an icon (use only 32px icons)*/
  svgUrl?: string;
  /** button color BLUE | WHITE */
  variant?: ButtonVariant;
  /** margin */
  margin?: string[];
  /** Sets label for button when spinner is true */
  spinnerLabel?: string;
  /** if modal is true */
  isModal?: boolean;
  /** modal children */
  modalChildren?: any;
  /** Size for the modal */
  modalSize?: ModalSize;
  /* modal to shopw video */
  modalVideoContent?: any;
  /** if newtab is true */
  isNewTab?: boolean;
  /** optional acessibility text if it is different from button text*/
  accessibilityText?: string;
  /** triggers modal type */
  triggerEvent?: string;
  /** object to passthrough to trigger function */
  triggerData?: any;
  /** reporting on click by default false*/
  eventReporting?: boolean;
  /** additional analytics data*/
  additionalAnalyticsData?: any;
};

export const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  | ButtonProps
  | React.ButtonHTMLAttributes<HTMLButtonElement>
  | React.AnchorHTMLAttributes<HTMLAnchorElement>
>(
  (
    {
      children = null,
      disabled = false,
      fullWidth = false,
      mobileFullWidth = false,
      href,
      isNavigational,
      metrics = undefined,
      onDark = false,
      size = ButtonSize.LARGE,
      spinner = false,
      spinnerWithoutLabel = false,
      svgNodeStr,
      svgUrl,
      ariahidden,
      variant = children !== '' ? ButtonVariant.BLUE : null,
      margin = [],
      spinnerLabel,
      isModal,
      modalChildren,
      modalSize,
      modalVideoContent,
      isNewTab,
      accessibilityText,
      triggerEvent,
      triggerData = {},
      eventReporting = false,
      additionalAnalyticsData,
      ...props
    }: ButtonProps &
      React.ButtonHTMLAttributes<HTMLButtonElement> &
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
    ref
  ) => {
    const [disabledState, setDisabledState] = React.useState(disabled);
    React.useEffect(() => {
      setDisabledState(!!disabled);
    }, [disabled]);

    delete props.className;
    delete props.style;
    delete props.role;
    delete props.type;
    let modalVideoData;
    const sendAnalyticsEvent = (e: any) => {
      const lnkname =
        additionalAnalyticsData?.linkName != undefined
          ? additionalAnalyticsData.linkName
          : e.target?.innerText;
      const destUrl =
        additionalAnalyticsData?.linkDestinationUrl != undefined
          ? additionalAnalyticsData.linkDestinationUrl
          : e.target?.href;
      const lnkPos =
        additionalAnalyticsData?.linkPosition != undefined
          ? additionalAnalyticsData.linkPosition
          : 'Body';
      if (additionalAnalyticsData) {
        delete additionalAnalyticsData.linkName;
        delete additionalAnalyticsData.linkDestinationUrl;
        delete additionalAnalyticsData.linkPosition;
      }
      const data = {
        linkDestinationUrl: destUrl,
        linkName: lnkname,
        linkPosition: lnkPos,
        ...additionalAnalyticsData,
      };
      const lnkClickObj = {
        eventCode: 'Link_Click',
        eventAction: 'linkClick',
        additionalData: data,
      };
      docComplete(() => {
        AnalyticsEvent(lnkClickObj);
      });
    };
    const classNames: string[] = [variant, margin?.join(' ')];
    const windowSize = useWindowSize();
    const isMobile = windowSize?.mobile;
    const { modal, addToCart, setModal } = useEventContext();
    if (size) {
      classNames.push(size);
    }
    if (onDark) {
      classNames.push('on-dark');
    }
    if (fullWidth) {
      classNames.push('btn-full-width');
    }
    if (mobileFullWidth && isMobile) {
      classNames.push('btn-full-width');
    }

    const Element: any = href ? 'a' : 'button';
    const buttonId = useUniqueId({disabled,fullWidth,variant,isNavigational,metrics,onDark,size,spinner,spinnerWithoutLabel,svgNodeStr,svgUrl,spinnerLabel,modalChildren,isNewTab,triggerEvent,triggerData,eventReporting,additionalAnalyticsData},Element, children) //props need to be pass for unique id 
    const [showModal, setShowModal] = useState(false);
    const [modalHeading, setModalHeading] = useState(null);
    const [modalBody, setModalBody] = useState(null);
    const [showVideo, setShowVideo] = useState(null);
    const [basePgLdData, setBasePgLdData] = useState(null);
    const handleCallbackForVideo = (isVideoDone) => {
      if (!isVideoDone) setShowVideo(null);
    };
    const closeModal = () => {
      setShowModal(false);
      const closeData = {
        linkName: "Close Icon",
        linkPosition: "modal",
      };
      const lnkClickObj = {
        eventCode: "Link_Click",
        eventAction: "linkClick",
        additionalData: closeData,
      };
      docComplete(() => {
        AnalyticsEvent(lnkClickObj);
      });
      if (basePgLdData?.eventAction) {
        const pageData = {
          datatransform: false,
          'page.category.applicationName': basePgLdData["page.category.applicationName"],
          'page.category.pageFunction': basePgLdData["page.category.pageFunction"],
          'page.category.siteSection': basePgLdData["page.category.siteSection"],
          'page.location.url': basePgLdData["page.location.url"],
          'page.pageInfo.friendlyPageName': basePgLdData["page.pageInfo.friendlyPageName"],
          'page.pageInfo.lineOfBusiness': basePgLdData["page.pageInfo.lineOfBusiness"],
          'page.pageInfo.pageTitle': basePgLdData["page.pageInfo.pageTitle"],
          'page.pageInfo.flowCode': basePgLdData["page.pageInfo.flowCod"],
        };
        docComplete(() => {
          AnalyticsEvent({
            "eventCode": "Page_Load",
            "eventAction": "pageLoad",
            "additionalData": pageData
          });
        });
      }
    };
    const getModalData = (e) => {
      e.preventDefault();
      const modalTitle = modalChildren?.modalTitle
        ? HeadingInterface.initWithSanity(
            'modalTitle',
            modalChildren?.modalTitle
          )
        : undefined;
      const modalContent = modalChildren?.modalContent
        ? TextInterface.initWithSanity(
            'modalContent',
            modalChildren?.modalContent
          )
        : undefined;
      modalTitle && setModalHeading(modalTitle?.toJsx());
      modalContent && setModalBody(modalContent?.toJsx());
      setShowModal(true);
    };
    const legalModalPageLoad = {
      'page.location.url': `${href}`,
      'page.pageInfo.friendlyPageName': `UF ${children} Modal Pg`,
      'page.pageInfo.flowCode': "DSUNK",
    };
    return (
      <>
        {(spinner || children || spinnerLabel) && (
          <Element
            ref={ref}
            id={buttonId}
            onClick={async (e: any) => {
              setBasePgLdData(window["AnalyticsNotificationFramework"]?.getLandingPageData() || {})
              if (modalVideoContent && modalVideoContent.videoId) {
                modalVideoData = BackgroundVideoInterface.initWithSanity(
                  'modalBgContent',
                  { modal: false, ...modalVideoContent }
                );
                setShowVideo(modalVideoData);
              }
              if (isModal) {
                getModalData(e);
              }
              if (disabledState) {
                e.preventDefault();
                return;
              }
              if (triggerEvent) {
                switch (triggerEvent) {
                  // case 'firstnetModal': {
                  // keeping this for backwards compaitble, if feed gets updated to "useModal", can remove
                  //   setModal?.({
                  //     type: 'firstnetModal',
                  //   });
                  //   break;
                  // }
                  // case 'useModal': {
                  //   setModal?.(triggerData);
                  //   break;
                  // }
                  case 'addToCart': {
                    e.preventDefault();
                    setDisabledState(true);
                    const result = await addToCart?.(triggerData);
                    // if not successful, may want to turn button back on
                    // setDisabledState(result.success);
                    break;
                  }
                  case 'emailCaptureModal': {
                    let tmpCompData = triggerData;
                    if (windowSize?.mobile) {
                      tmpCompData = triggerData["viewport-mobile"] || triggerData;
                    } else if (windowSize?.tablet) {
                      tmpCompData = triggerData["viewport-tablet"] || triggerData;
                    }
                    const modalData = {
                      type: triggerEvent,
                      modalProps: {
                        size: ModalSize.XLARGE,
                        additionalAnalyticsData: {
                          'page.location.url': "/emailsubscription/modal",
                          'page.pageInfo.friendlyPageName': `UF Emailsubscription Modal Pg`,
                          'page.pageInfo.flowCode': "DSUNK",
                        }
                      },
                      compProps: tmpCompData,
                      basePageData: window["AnalyticsNotificationFramework"]?.getLandingPageData() || {},
                    };
                    setModal(modalData);
                    break;
                  }
                  default: {
                    modal?.setType?.(triggerEvent);
                  }
                }
                // modal.setType(triggerEvent);
              }
              metrics?.onClick?.(e);
              props.onClick?.(e);
              if (eventReporting && href) {
                sendAnalyticsEvent(e);
              }
            }}
            onKeyDown={(e: any) => {
              if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                if (isModal) {
                  getModalData(e);
                }
                if (disabledState) {
                  e.preventDefault();
                  return;
                }
                metrics?.onClick?.(e);
              }
              props.onKeyDown?.(e);
            }}
            className={classNames.join(' ')}
            target={isNewTab ? '_blank' : undefined}
            disabled={disabledState || spinner}
            role={isNavigational && !href ? 'link' : undefined}
            type={href ? undefined : 'button'}
            aria-label={accessibilityText}
            {...props}
            href={
              href && disabledState !== true
                ? href
                : href && disabledState
                ? null
                : undefined
            }
          >
            {spinner ? (
              <svg
                viewBox="0 0 43 43"
                className={`duc-btn-spinner `}
                aria-label={'Loading'}
              >
                <g>
                  <circle
                    className="duc-btn-spinner-inner"
                    r="20"
                    cx="21.5"
                    cy="21.5"
                    fill="none"
                  ></circle>
                  <circle
                    className="duc-btn-spinner-outer"
                    r="20"
                    cx="21.5"
                    cy="21.5"
                    fill="none"
                  ></circle>
                </g>
              </svg>
            ) : spinnerWithoutLabel ? (
              <svg
                viewBox="0 0 43 43"
                className={`duc-btn-spinner absolute loading-spinner`}
                aria-label={'Loading'}
                style={{ margin: 0 }}
              >
                <g>
                  <circle
                    className="duc-btn-spinner-inner"
                    r="20"
                    cx="21.5"
                    cy="21.5"
                    fill="none"
                  ></circle>
                  <circle
                    className="duc-btn-spinner-outer"
                    r="20"
                    cx="21.5"
                    cy="21.5"
                    fill="none"
                  ></circle>
                </g>
              </svg>
            ) : svgNodeStr || svgUrl ? (
              <IconSvg
                aria-hidden="true"
                svgNodeStr={svgNodeStr}
                svgUrl={svgUrl}
              />
            ) : null}
            {spinnerLabel && spinner ? spinnerLabel : children}
          </Element>
        )}
        {isModal && showModal && (
          <Modal
            size={modalSize || ModalSize.MEDIUM}
            responsiveFullScreen={false}
            onClose={() => {
              closeModal();
            }}
            inScrim={false}
            additionalAnalyticsData={legalModalPageLoad}
          >
            <Modal.Header>{modalHeading}</Modal.Header>
            <Modal.Body>{modalBody}</Modal.Body>
          </Modal>
        )}
        {showVideo && (
          <>
            <Modal
              size={ModalSize.FULLSCREEN}
              responsiveFullScreen={false}
              close={false}
              onClose={() => {
                setShowVideo(modalVideoData);
              }}
              inScrim={false}
            >
              <Modal.Body>
                <BackgroundVideo
                  {...showVideo.toProps()}
                  {...(showVideo.toProps().playerView && {
                    parentCallback: handleCallbackForVideo,
                  })}
                />
                <button
                  id="closeVideo"
                  title="Close Video"
                  arial-label="Close Video"
                  className="close-btn absolute z2 width-md-all height-md-all bg-white type-14 top10 radius-lg"
                  onClick={() => setShowVideo(modalVideoData)}
                  data-testid="CloseBtn"
                  style={{ right: '50px' }}
                >
                  <IconSvg
                    size={IconSvgSizes.MD}
                    svgNodeStr={`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path d="M17.41 16l4.25 4.24-1.42 1.42L16 17.41l-4.24 4.25-1.42-1.42L14.59 16l-4.25-4.24 1.42-1.42L16 14.59l4.24-4.25 1.42 1.42z"/></svg>`}
                  />
                </button>
              </Modal.Body>
            </Modal>
          </>
        )}
        <style jsx>
          {`
            .duc-btn-spinner g {
              animation: duc-btn-spinner 2.25s linear infinite;
              transform-origin: center center;
            }
            .duc-btn-spinner-inner {
              stroke-width: 3;
              stroke: #878c94;
              opacity: 0.25;
            }
            .duc-btn-spinner-outer {
              stroke-width: 3;
              stroke-dasharray: 89, 200;
              stroke-dashoffset: 0;
              stroke: #009fdb;
              animation: duc-btn-spinner-outer 1.6875s ease-in-out infinite;
              stroke-linecap: round;
            }
            @keyframes duc-btn-spinner {
              100% {
                transform: rotate(360deg);
              }
            }
            @keyframes duc-btn-spinner-outer {
              0% {
                stroke-dasharray: 1, 200;
                stroke-dashoffset: 0;
              }
              50% {
                stroke-dasharray: 89, 200;
                stroke-dashoffset: -35px;
              }
              100% {
                stroke-dasharray: 89, 200;
                stroke-dashoffset: -124px;
              }
            }
          `}
        </style>
      </>
    );
  }
);
